import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { InputSize } from '../input/types';
import { DropdownProps } from './Dropdown';
import classNames from 'classnames';
import { getZIndex } from '../../styles';
import { IconProps } from '../icons/generated';
import { Stack } from '../stack';
import { AriaFocusProps } from '../../types/common';
import { filterItemStartsWith } from '../../utils/shared';
import groupBy from 'lodash-es/groupBy';
import difference from 'lodash-es/difference';
import { FormHelperText } from '../form';
import { DropdownFilterLabel } from './DropdownFilterLabel';
import { DropdownFilterOptions, Kind } from './DropdownFilterOptions';
import {
  DEFAULT_DROPDOWN_FILTER_MIN_HEIGHT,
  DEFAULT_DROPDOWN_FILTER_MIN_WIDTH,
  DEFAULT_DROPDOWN_FILTER_WITH_FILTER_MIN_HEIGHT,
} from './constants';
import { useDropdownFloating, useDropdownInteractions } from './hooks';
import { DropdownFilterGroupOption, DropdownFilterOption } from './types';
import { semantic_spacing } from '../../foundation';

export interface DropdownFilterProps<OptionValue>
  extends Omit<
    DropdownProps<OptionValue>,
    'options' | 'renderLabel' | 'placeholder' | 'value' | 'onChange' | 'optionsMatchRefWidth'
  > {
  value?: OptionValue[];
  label: string;
  /** @default 'medium' */
  size?: InputSize;
  /**
   *  DropDownFilter 초기값
   *  초기화시, DefaultValues가 있을 경우 DefaultValues를 사용하고, 없을 경우 빈 배열을 사용합니다.
   */
  defaultValues?: OptionValue[];
  /**
   * 옵션을 selection 그룹으로 묶을때 사용합니다. selectionGroup이 있을 경우 options는 무시됩니다.
   */
  selectionGroup?: DropdownFilterGroupOption<OptionValue>[];
  /**
   * 각 옵션 값들은 라벨 앞 자동으로 체크박스가 생성되며, 선택된 옵션값은 체크박스로 표시됩니다.
   */
  options?: DropdownFilterOption<OptionValue>[];
  filterPlaceholder?: string;
  helperText?: string;
  /**
   * 버튼 좌측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props로 컬러를 변경할 수 있습니다. 기본 값은 semantic_colors.content.primary 입니다.
   *  - Icon 컴포넌트 size props로 사이즈를 변경할 수 있습니다. 기본 값은 16입니다.
   */
  startIcon?: ReactElement<IconProps>;
  /**
   * 옵션을 필터링하는 필터 옵션 노출 여부
   * - 필터 검색 input이 옵션 목록 상단으로 노출되며 입력받은 필터 값에 따라 옵션 목록이 필터링됩니다.
   *
   * @default false
   */
  showFilter?: boolean;
  /**
   * 옵션 라벨앞 셀렉션 요소 타입
   * - radio의 경우 selectionGroup내에서 단일 선택, selectionGroup이 없다면 전체 옵션중 단일 선택만 가능합니다.
   *
   * @default 'checkbox'
   */
  kind?: Kind;
  /**
   * showFilter true일 경우 필터 검색 방식을 커스텀할 수 있습니다.
   */
  optionFilter?: (inputValue: string, item: DropdownFilterOption<OptionValue>, index: number) => boolean;
  /**
   * DropdownFilter Option들의 전체 선택 여부
   * - showFilter 옵션 또는 selectionGroup이 있을 경우, selectionType이 'radio'일 경우 전체 선택은 무시됩니다.
   *
   * @default false
   */
  allCheck?: boolean;
  /** @default false */
  optionsMatchRefWidth?: boolean;
  onChange?: (value: OptionValue[]) => void;
}

export const DropdownFilter = <OptionValue extends string | number>({
  label,
  value: valueProp,
  defaultValues = [],
  selectionGroup = [],
  options = [],
  width,
  size: sizeProp = 'medium',
  startIcon,
  disabled,
  status,
  zIndex = getZIndex('dropdown'),
  maxHeight,
  className,
  filterPlaceholder,
  optionsMatchRefWidth = false,
  autoPlacement: autoPlacementProps = true,
  unstableFocusVisible,
  showFilter = false,
  helperText,
  kind = 'checkbox',
  optionFilter = filterItemStartsWith,
  allCheck = false,
  keyExtractor,
  onChange,
}: DropdownFilterProps<OptionValue> & AriaFocusProps) => {
  const [opened, setOpened] = useState(false);
  const optionListRef = useRef<Array<HTMLElement | null>>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedList, setSelectedList] = useState<OptionValue[]>(defaultValues ?? valueProp ?? []);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const selectedIndex = optionListRef?.current.findIndex(
    (option) => option?.dataset['value'] === selectedList[selectedList.length - 1],
  );

  const is_all_checked = selectedList.length === options.length;

  const handleChange = (item_value: OptionValue) => {
    if (kind === 'checkbox') {
      const is_checked = !selectedList.includes(item_value);
      const new_values = is_checked
        ? [...selectedList, item_value]
        : selectedList.filter((value) => String(value) !== String(item_value));
      setSelectedList(new_values);
    } else {
      if (selectionGroup.length > 0) {
        const find_section_group = selectionGroup.find((group) =>
          group.options.find((option) => option.value === item_value),
        );
        const difference_values = difference(
          selectedList,
          (find_section_group?.options ?? []).map((option) => option.value),
        );
        setSelectedList([...difference_values, item_value]);
      } else {
        setSelectedList([item_value]);
      }
    }
  };

  const handleAllCheck = () => {
    is_all_checked ? setSelectedList([]) : setSelectedList(options.map(({ value }) => value));
    setFocusIndex(null);
  };

  const filteredOptions = useMemo(() => {
    const targetOptions =
      selectionGroup.length > 0
        ? selectionGroup.flatMap((group) =>
            group.options.map((option) => ({
              ...option,
              selectionKey: group.key,
            })),
          )
        : options;
    return targetOptions.filter((option, index) => optionFilter(searchValue, option, index));
  }, [options, selectionGroup, optionFilter, searchValue]);

  const computedOptions: Array<DropdownFilterGroupOption<OptionValue> | DropdownFilterOption<OptionValue>> =
    useMemo(() => {
      if (selectionGroup.length > 0) {
        const grouped = groupBy(filteredOptions, 'selectionKey');
        return Object.keys(grouped).map((key) => {
          const items = grouped[key];
          return {
            label: selectionGroup.find((group) => group.key === key)?.label ?? '-',
            key: key as OptionValue,
            options: items.map(({ label, value }) => ({ label, value })),
          };
        });
      } else {
        return filteredOptions;
      }
    }, [filteredOptions]);

  const { refs, floatingStyles, context } = useDropdownFloating({
    open: opened,
    onOpenChange: setOpened,
    autoPlacement: autoPlacementProps,
    sizeMiddlewareOptions: {
      apply({ rects, elements, availableWidth, availableHeight }) {
        Object.assign(elements.floating.style, {
          width: optionsMatchRefWidth
            ? rects.reference.width < DEFAULT_DROPDOWN_FILTER_MIN_WIDTH
              ? `${DEFAULT_DROPDOWN_FILTER_MIN_WIDTH}px`
              : `${rects.reference.width}px`
            : `${DEFAULT_DROPDOWN_FILTER_MIN_WIDTH}px`,
          maxWidth: !optionsMatchRefWidth ? `${availableWidth}px` : undefined,
          maxHeight:
            maxHeight &&
            `${
              availableHeight < maxHeight
                ? availableHeight - 20
                : showFilter
                ? DEFAULT_DROPDOWN_FILTER_WITH_FILTER_MIN_HEIGHT + maxHeight
                : DEFAULT_DROPDOWN_FILTER_MIN_HEIGHT + maxHeight
            }px`,
        });
      },
    },
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useDropdownInteractions({
    context,
    disabledInteractions: ['useTypeahead'],
    useClickProps: {
      enabled: !disabled,
    },
    useListNavigationProps: {
      listRef: optionListRef,
      activeIndex: focusIndex,
      selectedIndex,
      onNavigate: setFocusIndex,
    },
  });

  useEffect(() => {
    setSelectedList(valueProp ?? []);
  }, [opened, valueProp]);

  useEffect(() => {
    setSearchValue('');
    setFocusIndex(null);
  }, [opened]);

  return (
    <>
      <Stack direction='column' gap={semantic_spacing.helper_text.gap.desktop}>
        <DropdownFilterLabel
          {...getReferenceProps({
            'ref': refs.setReference,
            'className': classNames('pds-dropdown-filter-label', className),
            'aria-label': label,
            width,
            disabled,
            'selected': selectedList.length !== 0,
            'tabIndex': 0,
          })}
          startIcon={startIcon}
          expanded={opened}
          size={sizeProp}
          status={status}
        >
          {label}
        </DropdownFilterLabel>
        {helperText && <FormHelperText status='info'>{helperText}</FormHelperText>}
      </Stack>
      <DropdownFilterOptions
        refs={refs}
        size={sizeProp}
        setRefs={(node, index) => {
          optionListRef.current[index] = node;
        }}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        onClose={() => {
          setFocusIndex(null);
          setOpened(false);
        }}
        zIndex={zIndex}
        filterPlaceholder={filterPlaceholder}
        unstableFocusVisible={unstableFocusVisible}
        keyExtractor={keyExtractor}
        context={context}
        focusIndex={focusIndex}
        showFilter={showFilter}
        isAllCheck={allCheck}
        isOptionGroup={selectionGroup.length > 0}
        options={computedOptions}
        value={selectedList}
        searchValue={searchValue}
        onChangeSearchValue={setSearchValue}
        selectedList={selectedList}
        kind={kind}
        onConfirm={(values) => {
          onChange?.(values);
          setSelectedList(values);
        }}
        onChange={handleChange}
        onAllCheck={handleAllCheck}
        noDataText={'일치하는 항목이 없습니다.'}
        setFocusIndex={setFocusIndex}
        defaultValues={defaultValues}
      />
    </>
  );
};
